import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import * as validators from '../../util/validators';
import { FieldTextInput, Form, NamedLink, PrimaryButton } from '../../components';
import facebookLogo from '../../assets/facebook.png';
import googleLogo from '../../assets/google_logo.png';
import appleLogo from '../../assets/apple-logo.svg';
import SignUpField from './SignUpField';
import TurnstileField from './TurnstileField';
import { isCaptchaEnabled } from '../../util/captcha';

import css from './SignupForm.css';
import AppleSignin from 'react-apple-signin-auth';
import config from '../../config';
import { createRawPropertiesForGTM } from '../../util/gtm/gtmCreateProperties';
import { pushGTMSignupForm } from '../../util/gtm/gtmHelpers';
import { EVENT_SIGNUP_SOCIAL_APPLE } from '../../util/gtm/gtmConstants';
import { GTAG_METHODS, GTAG_ACTIONS, gSend } from '../../util/gtag';

const KEY_CODE_ENTER = 13;

const SignupFormComponent = props => (
  <FinalForm
    {...props}
    initialValues={{ email: props.initEmail, ...props.initialValues }}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        form,
        handleSubmit,
        inProgress,
        facebook,
        google,
        invalid,
        values,
        intl,
        onOpenTermsOfService,
        submitButtonId,
        touched,
        errors,
        handleChange,
        location,
        from,
        signupError,
      } = fieldRenderProps;

      // email
      const emailLabel = intl.formatMessage({
        id: 'SignupForm.emailLabel',
      });
      const emailPlaceholder = intl.formatMessage({
        id: 'SignupForm.emailPlaceholder',
      });
      const emailRequiredMessage = intl.formatMessage({
        id: 'SignupForm.emailRequired',
      });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'SignupForm.emailInvalid',
      });
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      // Hold submit until Turnstile has issued a token, but only where captcha
      // is actually configured — otherwise signup would be unusable.
      const captchaRequired = isCaptchaEnabled();
      const submitDisabled =
        invalid || submitInProgress || (captchaRequired && !values.captchaToken);

      const termsLink = (
        <NamedLink newTab={true} name="TermsOfServicePage">
          <FormattedMessage id="SignupForm.termsAndConditionsLinkText" />
        </NamedLink>
      );

      const policyLink = (
        <NamedLink newTab={true} name="PrivacyPolicyPage">
          <FormattedMessage id="SignupForm.privacyPolicyLinkText" />
        </NamedLink>
      );

      const getLocationState = location
        ? {
            state: location.state,
          }
        : {};

      return (
        <>
          <Form className={classes} onSubmit={handleSubmit}>
            <div>
              <FieldTextInput
                className={css.formInput}
                type="email"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                disabled={!!props.initEmail}
                label={emailLabel}
                placeholder={emailPlaceholder}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />
              <SignUpField
                intl={intl}
                formId={formId}
                values={values}
                touched={touched}
                errors={errors}
              />
            </div>

            <TurnstileField
              className={css.captcha}
              errorClassName={css.captchaError}
              loadErrorMessage={<FormattedMessage id="SignupForm.captchaLoadFailed" />}
              onToken={token => form.change('captchaToken', token)}
              resetSignal={signupError}
            />

            <div className={css.bottomWrapper}>
              <PrimaryButton
                className={css.signInButton}
                id={submitButtonId}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
              >
                <FormattedMessage id="SignupForm.signUp" />
              </PrimaryButton>
              <p className={css.bottomWrapperText}>
                <span className={css.termsText}>
                  <FormattedMessage
                    id="SignupForm.termsAndConditionsAcceptText"
                    values={{ termsLink, policyLink }}
                  />
                </span>
              </p>
            </div>
          </Form>
          <div className={css.loginWith}>
            <span className={css.text}>
              <h3>
                <span>or sign up with</span>
              </h3>
            </span>
            <div className={classNames(css.loginUsing, css.facebook)}>
              <a onClick={facebook}>
                <span>
                  <img src={facebookLogo} alt={'facebook logo'} />
                </span>
                <span className={classNames(css.btnText, css.facebook)}>Sign in with Facebook</span>
              </a>
            </div>
            <div className={classNames(css.loginUsing, css.google, css.googleBorder)}>
              <a onClick={google}>
                <span>
                  <img src={googleLogo} alt={'google logo'} />
                </span>
                <span className={classNames(css.btnText, css.google)}>Sign in with Google</span>
              </a>
            </div>
            <AppleSignin
              authOptions={{ ...config.appleAuthOptions, state: from }}
              uiType="dark"
              className="apple-auth-btn"
              noDefaultStyle={false}
              buttonExtraChildren="Continue with Apple"
              skipScript={false}
              onSuccess={response => {
                const properties = createRawPropertiesForGTM({
                  props: this.props,
                  button: {
                    buttonId: 'countinue-with-apple',
                  },
                });
                // pushGTMSignupForm(properties, EVENT_SIGNUP_SOCIAL_APPLE);
                gSend(GTAG_ACTIONS.ACTION_SIGNUP, { method: GTAG_METHODS.APPLE_SIGNUP });
              }}
              render={props => (
                <div className={classNames(css.loginUsing, css.apple)} {...props}>
                  <a>
                    <span>
                      <img src={appleLogo} alt={'apple logo'} />
                    </span>
                    <span className={classNames(css.btnText, css.apple)}>Sign in with Apple</span>
                  </a>
                </div>
              )}
            />
            <div className={css.signupButton} onClick={() => handleChange(true)}>
              Already have an account?
              <NamedLink name="LoginPage" onClick={() => handleChange(true)}>
                <a> Log In</a>
              </NamedLink>
            </div>
          </div>
        </>
      );
    }}
  />
);

SignupFormComponent.defaultProps = { inProgress: false };

const { bool, func, string } = PropTypes;

SignupFormComponent.propTypes = {
  inProgress: bool,
  facebook: func,
  onOpenTermsOfService: func.isRequired,
  google: func,
  signUpCode: string,

  initEmail: string,
  submitButtonId: string,
  // from injectIntl
  intl: intlShape.isRequired,
};

const SignupForm = compose(injectIntl)(SignupFormComponent);
SignupForm.displayName = 'SignupForm';

export default SignupForm;
